// Reading a flag from a component.
//
// One fetch for the whole page, shared by every reader, and a stable answer per
// flag for the life of the tab. The rollouts arrive once; the *decision* is
// local arithmetic, so a component asking on every render costs nothing and a
// flag cannot change under a mounted tree.

import { useEffect, useState, useSyncExternalStore } from "react";

import { FLAGS, type Flag, type FlagId } from "src/engine/flags/flags";
import { flagIsOn, flagVariant, forced } from "src/engine/flags/visitor";

/** Where the deployment publishes what it is running. See `server/flags.ts`. */
const ENDPOINT = "/api/flags";

/**
 * The rollouts, fetched once per tab and shared.
 *
 * **Module state, and here it is right.** `src/engine/rules/` forbids it because two
 * clients derive one board from one log; this is a cache of a *deployment's*
 * configuration, identical for everybody, read by nothing that plays. One
 * fetch rather than one per component is the whole point — a dozen readers on
 * a page should not be a dozen requests.
 */
let rollouts: Record<string, number> | null = null;
let asked = false;
const listeners = new Set<() => void>();

function load(): void {
  if (asked) return;
  asked = true;
  void fetch(ENDPOINT, { credentials: "same-origin" })
    .then((response) => (response.ok ? response.json() : null))
    .then((body: { rollouts?: Record<string, number> } | null) => {
      // A malformed answer is no answer: `flagIsOn` falls back to the shipped
      // default, which is the configuration this build was released with.
      if (body?.rollouts && typeof body.rollouts === "object") rollouts = body.rollouts;
      for (const listener of listeners) listener();
    })
    // **A dev server has no `/api`, and neither does an offline tab.** Both get
    // the shipped defaults rather than an error: a flag system that takes the
    // page down when it cannot reach its own configuration is worse than no
    // flag system.
    .catch(() => {
      for (const listener of listeners) listener();
    });
}

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  load();
  return () => listeners.delete(listener);
};

/**
 * Is this feature on for this visitor?
 *
 * **Held for the life of the tab**, deliberately. The rollouts can only ever
 * arrive once, but a component that re-read the answer would still be a
 * component whose feature could vanish mid-session if anything about the key
 * changed — and a feature that comes and goes while somebody is using it is
 * the failure a stable bucket exists to prevent. So the first answer after the
 * fetch is the answer.
 */
export function useFlag(id: FlagId): boolean {
  const live = useSyncExternalStore(
    subscribe,
    () => rollouts,
    () => rollouts
  );
  const [locked, setLocked] = useState<boolean | null>(null);
  useEffect(() => {
    // The first read once the deployment has answered, and then never again.
    if (locked === null && (live !== null || asked)) setLocked(flagIsOn(id, live));
  }, [id, live, locked]);
  return locked ?? flagIsOn(id, live);
}

/**
 * The arm of a multi-way flag this tab is on, locked for its life.
 *
 * `useFlag`'s twin and locked for the identical reason: an arm that changed
 * under a game in progress would move the rules out from under the player —
 * here quite literally, since this one decides how long a camp waits.
 */
export function useVariant<T extends string | number, F>(id: FlagId, fallback: F): T | F {
  const live = useSyncExternalStore(
    subscribe,
    () => rollouts,
    () => rollouts
  );
  const [locked, setLocked] = useState<T | F | null>(null);
  useEffect(() => {
    if (locked === null && (live !== null || asked)) setLocked(flagVariant<T, F>(id, fallback, live));
  }, [id, fallback, live, locked]);
  return locked ?? flagVariant<T, F>(id, fallback, live);
}

/** Where one flag stands for this tab, for a dev tool that lists them. */
export interface FlagStanding {
  id: FlagId;
  /** What the flag does — the table's own sentence. */
  detail: string;
  on: boolean;
  /** The arm a multi-way flag landed on; null is the control, or a boolean flag. */
  arm: string | number | null;
  /** The arms a multi-way flag offers; absent for a plain boolean. */
  variants?: readonly (string | number)[];
  rollout: number;
  /** A `?flag=` override is deciding, rather than the bucket. */
  forced: boolean;
}

/** Every flag and where it stands, for a dev tool that wants to list them. */
export const flagStates = (): FlagStanding[] =>
  FLAGS.map((entry) => {
    // Through `Flag`, for `visitor.ts`'s reason: the table's literal types drop
    // `variants` from the boolean entries, so the union has no such property.
    const flag: Flag = entry;
    return {
      id: flag.id as FlagId,
      detail: flag.detail,
      on: flagIsOn(flag.id as FlagId, rollouts),
      arm: flagVariant<string | number, null>(flag.id as FlagId, null, rollouts),
      variants: flag.variants,
      rollout: rollouts?.[flag.id] ?? flag.rollout,
      forced: forced(flag.id as FlagId) !== null
    };
  });

/**
 * `flagStates`, live: recomputed when the deployment's rollouts arrive.
 *
 * Deliberately **not** locked the way `useFlag` is — this is the dev panel
 * asking "what is this tab seeing", and a reading that disagreed with the
 * deployment's answer would send somebody debugging a rollout that is fine.
 * The panel changes nothing without a reload, so nothing can shift under a
 * game because of it.
 */
export function useFlagStandings(): FlagStanding[] {
  useSyncExternalStore(
    subscribe,
    () => rollouts,
    () => rollouts
  );
  return flagStates();
}
